export type AudioControlCommand =
  | { action: 'play' | 'pause' | 'skip_forward' | 'skip_backward' | 'next_chapter' | 'previous_chapter' }
  | { action: 'seek'; time: number; chapterId?: string };

const SIMPLE_ACTIONS = new Set<AudioControlCommand['action']>([
  'play',
  'pause',
  'skip_forward',
  'skip_backward',
  'next_chapter',
  'previous_chapter',
]);

export const parseAudioControlCommand = (value: unknown): AudioControlCommand | null => {
  if (!value || typeof value !== 'object') return null;
  const data = value as Record<string, unknown>;
  if (data.action === 'seek') {
    if (typeof data.time !== 'number' || !Number.isFinite(data.time)) return null;
    return {
      action: 'seek',
      time: data.time,
      ...(typeof data.chapterId === 'string' ? { chapterId: data.chapterId } : {}),
    };
  }
  if (typeof data.action === 'string' && SIMPLE_ACTIONS.has(data.action as AudioControlCommand['action'])) {
    return { action: data.action as Exclude<AudioControlCommand['action'], 'seek'> };
  }
  return null;
};

interface AudioControlHandlers {
  rewindToCurrentSentence: () => boolean;
  play: () => void;
  pause: () => void;
  skipForward: () => void;
  skipBackward: () => void;
  nextChapter: (options: { resumePlayback: boolean }) => boolean;
  previousChapter: (options: { resumePlayback: boolean }) => boolean;
  seekAndPlay: (time: number, chapterId?: string) => void;
}

export const executeAudioControlCommand = (
  command: AudioControlCommand,
  handlers: AudioControlHandlers,
): void => {
  if (command.action === 'seek') {
    handlers.seekAndPlay(command.time, command.chapterId);
    return;
  }
  handlers.rewindToCurrentSentence();
  switch (command.action) {
    case 'play': handlers.play(); break;
    case 'pause': handlers.pause(); break;
    case 'skip_forward': handlers.skipForward(); break;
    case 'skip_backward': handlers.skipBackward(); break;
    case 'next_chapter':
      if (!handlers.nextChapter({ resumePlayback: true })) handlers.play();
      break;
    case 'previous_chapter':
      if (!handlers.previousChapter({ resumePlayback: true })) handlers.play();
      break;
  }
};
